/**
 * customers-tabs.tsx — Thanh tab của module Khách hàng. MỖI TAB LÀ MỘT ĐƯỜNG DẪN RIÊNG
 * (/customers, /customers/duplicates, /customers/extract-phones, /customers/segments) để phân quyền theo trang được:
 * tab hiện khi vai trò được phép VÀ có quyền xem trang đó (PAGE_VIEW_PERM, khớp backend permission-registry).
 */
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PAGE_VIEW_PERM } from '@/components/shared/nav-config'
import { useMyPermissions } from '@/hooks/use-settings'
import { i18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'
import type { Role } from '@/types/api'

export interface CustomerTab {
  to: string
  label: string
  /** Bỏ trống = mọi vai trò. */
  roles?: Role[]
}

export const CUSTOMER_TABS: CustomerTab[] = [
  { to: '/customers', get label() { return i18n.t('custList:tabs.list') } },
  { to: '/customers/duplicates', get label() { return i18n.t('custList:tabs.duplicates') } },
  { to: '/customers/extract-phones', get label() { return i18n.t('custList:tabs.extractPhones') } },
  { to: '/customers/segments', get label() { return i18n.t('custList:tabs.segments') }, roles: ['owner', 'admin', 'manager'] },
]

/** Tab vai trò + quyền hiện tại được xem. `perms` chưa tải xong → chỉ lọc theo vai trò (server vẫn chặn). */
export function customerTabsFor(role: Role | undefined, perms: Set<string> | undefined): CustomerTab[] {
  if (!role) return []
  return CUSTOMER_TABS.filter((t) => {
    if (t.roles && !t.roles.includes(role)) return false
    const need = PAGE_VIEW_PERM[t.to]
    return !need || !perms || perms.has(need)
  })
}

export function CustomersTabs({ className }: { className?: string }) {
  const { t } = useTranslation('custList')
  const role = useAuthStore((s) => s.user?.role)
  const { data: perms } = useMyPermissions()
  const tabs = customerTabsFor(role, perms)
  if (tabs.length < 2) return null
  return (
    <nav aria-label={t('tabs.aria')} className={cn('flex gap-1 overflow-x-auto border-b', className)}>
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end
          className={({ isActive }) =>
            cn(
              '-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
              isActive ? 'border-primary font-medium text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  )
}
